<script lang="ts">
  import FormField from '$src/js/modules/ui/components/form/FormField.svelte';
  import { MapboxService } from '$src/js/modules/mapbox/MapboxService';
  import type { ProgramItemTypeAdd, R2REvent } from '$src/types/events';
  import { initBouncerValidation } from '$src/js/modules/form-validation/init-bouncer';
  import { onMount } from 'svelte';
  import type { ResponseStatus } from '$src/types/forms';

  const {
    event,
  }: {
    event: R2REvent;
  } = $props();

  let loading = $state(false);
  let showForm = $state(false);
  let errorMessage = $state('');
  let locationErrorMessage = $state('');
  const mapboxService = new MapboxService();

  const getCoordinates = async () => {
    try {
      const address = `${eventData.location.street}, ${eventData.location.zipcode} ${eventData.location.city}, ${eventData.location.country}`;
      const geoDataResponse =
        await mapboxService.getGeoLocationFromString(address);

      if (
        typeof geoDataResponse.lng === 'number' &&
        typeof geoDataResponse.lat === 'number'
      ) {
        eventData.location.lng = geoDataResponse.lng;
        eventData.location.lat = geoDataResponse.lat;
        eventData.province = geoDataResponse.province ?? '-';

        locationErrorMessage = '';
      } else {
        locationErrorMessage = 'Kon geen coördinaten vinden voor dit adres';
      }
    } catch {
      locationErrorMessage =
        'Er ging iets mis bij het ophalen van de coördinaten';
    }
  };

  const modifyDateToTimeString = (date: string) => {
    const dateObj = new Date(date);

    return `${dateObj.getHours().toString().padStart(2, '0')}:${dateObj.getMinutes().toString().padStart(2, '0')}`;
  };

  const modifyTimeByAgeGroup = (ageGroup: string, value: string) => {
    const index = eventData.programAgeGroups.findIndex(
      (programAgeGroup) => programAgeGroup.ageGroup === ageGroup
    );

    const updatedProgramAgeGroups = [...eventData.programAgeGroups];
    updatedProgramAgeGroups[index] = {
      ageGroup: ageGroup,
      timeStart: value,
    };

    eventData = {
      ...eventData,
      programAgeGroups: updatedProgramAgeGroups,
    };
  };

  interface EventDataType {
    id: number;
    location: {
      name: string;
      street: string;
      zipcode: string;
      city: string;
      country: string;
      lat: number;
      lng: number;
    };
    province: string | null;
    dateEvent: string;
    programAgeGroups: ProgramItemTypeAdd[];
  }

  let eventData = $state<EventDataType>({
    id: event.id,
    location: {
      name: event.location.name,
      street: event.location.street,
      zipcode: event.location.zip,
      city: event.location.city,
      country: event.location.country.toLowerCase(),
      lat: event.location.lat,
      lng: event.location.lng,
    },
    province: event.province,
    dateEvent: event.date.date,
    programAgeGroups: event.programAgeGroups.map((programAgeGroup) => ({
      ageGroup: programAgeGroup.ageGroup,
      timeStart: modifyDateToTimeString(programAgeGroup.timeStart.date),
    })),
  });

  const handleFormSubmit = async (event: Event) => {
    const form = event.target as HTMLFormElement;

    if (
      !(form instanceof HTMLFormElement) ||
      form.dataset.validateForm !== 'rdrEvent'
    ) {
      return;
    }

    event.preventDefault();
    loading = true;

    const formDataObject = new FormData();
    formDataObject.append('id', eventData.id.toString());
    formDataObject.append('location', JSON.stringify(eventData.location));
    formDataObject.append('province', eventData.province ?? '-');
    formDataObject.append('dateEvent', eventData.dateEvent);
    formDataObject.append(
      'programAgeGroups',
      JSON.stringify(eventData.programAgeGroups)
    );

    try {
      await new Promise((resolve) => setTimeout(resolve, 400));
      const response = await fetch('/api/activities/dbr/organize/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': window.csrfTokenValue,
        },
        body: JSON.stringify(Object.fromEntries(formDataObject.entries())),
      });

      const responseData = (await response.json()) as {
        status: ResponseStatus;
      };

      if (!response.ok || responseData.status !== 'success') {
        errorMessage =
          'Sorry, er is iets fout gegaan bij het indienen van het evenement. Probeer het opnieuw.';
        loading = false;
        return;
      }

      window.location.reload();
    } catch {
      errorMessage =
        'Sorry, er is iets fout gegaan bij het indienen van het evenement. Probeer het opnieuw.';
      loading = false;
    }
  };

  $effect(() => {
    if (showForm) {
      initBouncerValidation();
    }
  });

  onMount(() => {
    document.addEventListener('bouncerFormValid', (event) => {
      if (!eventData.dateEvent) {
        errorMessage = 'Datum is verplicht.';
        return;
      }

      void handleFormSubmit(event);
    });
  });
</script>

<div class="border-border-bottom-1 flex flex-col gap-6">
  <button
    class="btn-primary-large"
    onclick={() => {
      showForm = !showForm;
    }}>Wijzig evenement</button
  >

  {#if showForm}
    <div class="2xl:fluid-gap-[32] mb-6 flex flex-col gap-4 gap-8">
      <h3 class="heading-s">Wijzig het evenement</h3>

      <form
        class={`2xl:fluid-gap-[48] flex flex-col gap-12 ${loading ? 'loading-box loading-box--bottom' : ''}`}
        data-validate-form="rdrEvent"
      >
        <FormField
          background="black"
          type="singleDate"
          id="dateEvent"
          name="dateEvent"
          label="Datum"
          placeholder="Selecteer een datum"
          required={true}
          attributes={{
            dateObjects: event.date.date,
          }}
          bind:value={eventData.dateEvent}
        />

        <FormField
          background="black"
          type="text"
          label="Locatienaam"
          id="locationName"
          name="locationName"
          required={true}
          bind:value={eventData.location.name}
        />

        <div class="2xl:fluid-gap-[16] flex w-full gap-4">
          <FormField
            background="black"
            type="text"
            label="Straatnaam"
            id="locationStreet"
            name="locationStreet"
            required={true}
            bind:value={eventData.location.street}
            onBlur={getCoordinates}
          />
        </div>

        <div class="2xl:fluid-gap-[16] flex w-full gap-4">
          <FormField
            background="black"
            type="text"
            label="Postcode"
            id="locationzipcode"
            name="locationzipcode"
            required={true}
            bind:value={eventData.location.zipcode}
            onBlur={getCoordinates}
          />
          <FormField
            background="black"
            type="text"
            label="Plaatsnaam"
            id="locationCity"
            name="locationCity"
            required={true}
            bind:value={eventData.location.city}
            onBlur={getCoordinates}
          />
        </div>

        {#if locationErrorMessage}
          <span class="text-16px text-error">{locationErrorMessage}</span>
        {/if}

        <FormField
          background="black"
          type="select"
          label="Land"
          id="locationCountry"
          name="locationCountry"
          options={Object.entries(window.countries).map(([key, value]) => ({
            value: key,
            label: value,
          }))}
          required={true}
          onChange={getCoordinates}
          bind:value={eventData.location.country}
        />

        <div class="@2xl:fluid-gap-[16] flex flex-col gap-4">
          <h4 class="heading-s">Wijzig tijd per leeftijdsgroep</h4>
          {#each eventData.programAgeGroups as programAgeGroup}
            {#if window.ageGroups[programAgeGroup.ageGroup]}
              <FormField
                background="black"
                type="text"
                label={`Starttijd ${window.ageGroups[programAgeGroup.ageGroup]}`}
                id={`time_${programAgeGroup.ageGroup}`}
                name={`time_${programAgeGroup.ageGroup}`}
                required={true}
                value={programAgeGroup.timeStart}
                attributes={{
                  type: 'time',
                }}
                onChange={(e: Event) => {
                  const target = e.currentTarget as HTMLInputElement;
                  modifyTimeByAgeGroup(programAgeGroup.ageGroup, target.value);
                }}
              />
            {/if}
          {/each}
        </div>

        {#if errorMessage}
          <div
            class="text-14px 2xl:fluid-p-[16] border-error border-1 p-4 leading-[180%] font-medium"
          >
            {errorMessage}
          </div>
        {/if}
        <button class="btn-primary-large" type="submit">Wijzig</button>
      </form>
    </div>
  {/if}
</div>
